import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, X, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import ImageInput from './ImageInput';
import { TextField, TextArea, SelectField, Check } from './AdminFields';

const CATS = [['machines', 'Choosing machines'], ['nozzles', 'Nozzles'], ['air', 'Air supply'], ['dry-ice', 'Dry ice'], ['costs', 'Costs'], ['training', 'Training']];
const EMPTY = { title: '', slug: '', category: 'machines', excerpt: '', body: '', cover_image: '', related_machine_ids: [], related_links: [], order: 0, published: true };

const GuideForm = ({ initial, catalog, onClose, onSaved }) => {
    const [f, setF] = useState(initial);
    const [saving, setSaving] = useState(false);
    const upd = (k, v) => setF({ ...f, [k]: v });
    const toggleProduct = (id) => upd('related_machine_ids', f.related_machine_ids.includes(id) ? f.related_machine_ids.filter((x) => x !== id) : [...f.related_machine_ids, id]);
    const setLink = (i, patch) => upd('related_links', f.related_links.map((l, j) => (j === i ? { ...l, ...patch } : l)));
    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...f, order: parseInt(f.order, 10) || 0, related_links: f.related_links.filter((l) => l.label && l.to) };
            if (f.id) await api.put(`/admin/guides/${f.id}`, payload); else await api.post('/admin/guides', payload);
            toast.success('Guide saved');
            onSaved();
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Check the title and fields');
            setSaving(false);
        }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-guide-modal">
            <div className="w-full max-w-4xl bg-brand-surface border border-white/10 max-h-[calc(100vh-2rem)] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-white/10">
                    <h3 className="font-display font-black uppercase text-2xl">{f.id ? 'Edit' : 'New'} guide</h3>
                    <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="admin-guide-close"><X /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <TextField label="Title *" value={f.title} onChange={(v) => upd('title', v)} testid="gf-title" />
                        <TextField label="URL slug (blank = from title)" value={f.slug} onChange={(v) => upd('slug', v)} testid="gf-slug" />
                        <SelectField label="Topic" value={f.category} onChange={(v) => upd('category', v)} options={CATS} testid="gf-category" />
                        <TextField label="Order (lower shows first)" type="number" value={f.order} onChange={(v) => upd('order', v)} testid="gf-order" />
                    </div>
                    <TextArea label="Short summary (card + Google description)" rows={2} value={f.excerpt} onChange={(v) => upd('excerpt', v)} testid="gf-excerpt" />
                    <TextArea label="Guide text — blank line = new paragraph, '## ' = heading, '- ' = bullet, **bold**" rows={16} value={f.body} onChange={(v) => upd('body', v)} testid="gf-body" />
                    <ImageInput label="Cover image (optional)" value={f.cover_image} onChange={(v) => upd('cover_image', v)} testid="gf-cover" />
                    <div>
                        <div className="label-caps text-white/50 text-xs mb-2">Products to show under the guide</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                            {catalog.map((m) => (
                                <label key={m.id} className="flex items-center gap-2 text-sm text-white/80"><input type="checkbox" checked={f.related_machine_ids.includes(m.id)} onChange={() => toggleProduct(m.id)} data-testid={`gf-product-${m.id}`} />{m.name}</label>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="label-caps text-white/50 text-xs">Buttons at the end (first one is highlighted)</div>
                        {f.related_links.map((l, i) => (
                            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                                <TextField label="Button text" value={l.label} onChange={(v) => setLink(i, { label: v })} testid={`gf-link-label-${i}`} />
                                <TextField label="Link (e.g. /shop)" value={l.to} onChange={(v) => setLink(i, { to: v })} testid={`gf-link-to-${i}`} />
                                <button type="button" onClick={() => upd('related_links', f.related_links.filter((_, j) => j !== i))} className="p-2.5 border border-red-500/60 text-red-600"><Trash2 className="h-4 w-4" /></button>
                            </div>
                        ))}
                        <button type="button" onClick={() => upd('related_links', [...f.related_links, { label: '', to: '' }])} className="inline-flex items-center gap-1 label-caps text-brand-primary text-xs" data-testid="gf-link-add"><Plus className="h-4 w-4" /> Add button</button>
                    </div>
                    <Check label="Published" checked={f.published} onChange={(v) => upd('published', v)} testid="gf-published" />
                </div>
                <div className="flex justify-end gap-3 p-6 border-t border-white/10">
                    <button onClick={onClose} className="label-caps border border-white/20 hover:bg-white/10 px-5 py-3">Cancel</button>
                    <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-5 py-3 label-caps" data-testid="gf-save">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                    </button>
                </div>
            </div>
        </div>
    );
};

const GuidesAdmin = () => {
    const [items, setItems] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [editing, setEditing] = useState(null);
    const load = () => api.get('/admin/guides').then((r) => setItems(r.data));
    useEffect(() => { load(); api.get('/machines').then((r) => setCatalog(r.data)); }, []);
    const remove = async (g) => {
        if (!window.confirm(`Delete "${g.title}"?`)) return;
        await api.delete(`/admin/guides/${g.id}`);
        load();
    };
    return (
        <div data-testid="admin-guides">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display font-black uppercase text-3xl">Buying guides</h2>
                <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-4 py-2.5 label-caps" data-testid="admin-guides-new"><Plus className="h-4 w-4" /> New guide</button>
            </div>
            <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-guides-list">
                {items.length === 0 && <div className="p-6 text-white/50">No guides yet.</div>}
                {items.map((g) => (
                    <div key={g.id} className="p-4 flex items-center gap-4" data-testid={`admin-guide-${g.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-white truncate">{g.title}</div>
                            <div className="text-sm text-white/50">/knowledge/{g.slug} • {g.published ? 'Published' : 'Hidden'}</div>
                        </div>
                        <button onClick={() => setEditing({ ...EMPTY, ...g })} className="p-2 border border-white/10 hover:bg-white/10" data-testid={`admin-guide-edit-${g.id}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(g)} className="p-2 border border-red-500/60 text-red-600 hover:bg-red-500/10" data-testid={`admin-guide-delete-${g.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                ))}
            </div>
            {editing && <GuideForm initial={editing} catalog={catalog} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
};

export default GuidesAdmin;
